// src/pages/PodcastDetailManagement.js
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getPodcastById } from '../services/RssImportService';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../firebase';

function PodcastDetailManagement() {
  const { podcastId } = useParams();
  const navigate = useNavigate();
  const { currentUser, isCreator } = useAuth();
  
  const [podcast, setPodcast] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [editingEpisode, setEditingEpisode] = useState(null);

  useEffect(() => {
    async function loadPodcastData() {
      if (!currentUser || !isCreator) return;
      
      try {
        setLoading(true);
        
        // Get podcast data
        const podcastData = await getPodcastById(podcastId);
        if (!podcastData) {
          setError('Podcast not found');
          return;
        }
        
        // Verify ownership
        if (podcastData.creatorId !== currentUser.uid) {
          setError('You do not have permission to manage this podcast');
          return;
        }
        
        setPodcast(podcastData);
        
        // Get episodes from new episodes collection
        const episodesQuery = query(
          collection(db, 'episodes'),
          where('podcastId', '==', podcastId)
        );
        
        const episodesSnapshot = await getDocs(episodesQuery);
        const episodeArray = episodesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })).sort((a, b) => {
          const dateA = a.publishedAt?.seconds || 0;
          const dateB = b.publishedAt?.seconds || 0;
          return dateB - dateA; // Newest first
        });
        
        setEpisodes(episodeArray);
        
      } catch (err) {
        console.error('Error loading podcast:', err);
        setError('Failed to load podcast data');
      } finally {
        setLoading(false);
      }
    }
    
    loadPodcastData();
  }, [podcastId, currentUser, isCreator]);

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    try {
      if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        return new Date(timestamp.toDate()).toLocaleDateString();
      }
      return new Date(timestamp).toLocaleDateString();
    } catch (err) {
      return 'Invalid Date';
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!currentUser) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2>Authentication Required</h2>
        <p>Please log in to manage podcasts.</p>
        <Link to="/login" style={{ 
          display: 'inline-block',
          padding: '12px 24px',
          background: '#4a69dd',
          color: 'white',
          textDecoration: 'none',
          borderRadius: '6px'
        }}>
          Log In
        </Link>
      </div>
    );
  }

  if (!isCreator) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2>Creator Access Required</h2>
        <p>You need creator privileges to manage podcasts.</p>
        <Link to="/for-creators" style={{ 
          display: 'inline-block',
          padding: '12px 24px',
          background: '#4a69dd',
          color: 'white',
          textDecoration: 'none',
          borderRadius: '6px'
        }}>
          Become a Creator
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ 
          width: '50px',
          height: '50px',
          border: '3px solid #f3f3f3',
          borderTop: '3px solid #4a69dd',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 20px auto'
        }}></div>
        <p>Loading podcast data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2>Error</h2>
        <p style={{ color: '#e53935', marginBottom: '20px' }}>{error}</p>
        <Link to="/podcast-management" style={{ 
          display: 'inline-block',
          padding: '12px 24px',
          background: '#4a69dd',
          color: 'white',
          textDecoration: 'none',
          borderRadius: '6px'
        }}>
          Back to Podcast Management
        </Link>
      </div>
    );
  }

  if (!podcast) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2>Podcast Not Found</h2>
        <Link to="/podcast-management" style={{ 
          display: 'inline-block',
          padding: '12px 24px',
          background: '#4a69dd',
          color: 'white',
          textDecoration: 'none',
          borderRadius: '6px'
        }}>
          Back to Podcast Management
        </Link>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex',
        alignItems: 'center',
        gap: '20px',
        marginBottom: '30px',
        borderBottom: '1px solid #ddd',
        paddingBottom: '20px'
      }}>
        <div style={{ 
          width: '80px',
          height: '80px',
          borderRadius: '8px',
          overflow: 'hidden',
          flexShrink: 0
        }}>
          {podcast.imageURL ? (
            <img 
              src={podcast.imageURL} 
              alt={podcast.title}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <div style={{ 
              width: '100%',
              height: '100%',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '24px',
              fontWeight: 'bold'
            }}>
              {podcast.title ? podcast.title.charAt(0).toUpperCase() : '?'}
            </div>
          )}
        </div>
        
        <div style={{ flex: 1 }}>
          <h1 style={{ margin: '0 0 5px 0', fontSize: '28px' }}>{podcast.title}</h1>
          <p style={{ margin: '0 0 10px 0', color: '#666' }}>
            {episodes.length} episodes • Created {formatDate(podcast.createdAt)}
          </p>
          <div style={{ display: 'flex', gap: '10px' }}>
            <Link to="/podcast-management" style={{ 
              padding: '8px 16px',
              background: '#f5f5f5',
              color: '#333',
              textDecoration: 'none',
              borderRadius: '4px',
              border: '1px solid #ddd',
              fontSize: '14px'
            }}>
              ← Back to All Podcasts
            </Link>
            <button style={{ 
              padding: '8px 16px',
              background: '#4a69dd',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}>
              Sync RSS Feed
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div style={{ 
        display: 'flex',
        borderBottom: '1px solid #ddd',
        marginBottom: '30px'
      }}>
        {[
          { id: 'overview', label: 'Overview' },
          { id: 'episodes', label: 'Episodes' },
          { id: 'analytics', label: 'Analytics' },
          { id: 'settings', label: 'Settings' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: activeTab === tab.id ? '600' : '400',
              color: activeTab === tab.id ? '#4a69dd' : '#666',
              borderBottom: activeTab === tab.id ? '2px solid #4a69dd' : '2px solid transparent',
              transition: 'all 0.2s'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div>
          {/* Quick Stats */}
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '20px',
            marginBottom: '30px'
          }}>
            <div style={{ 
              background: 'white',
              padding: '20px',
              borderRadius: '8px',
              textAlign: 'center',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              border: '1px solid #e0e0e0'
            }}>
              <h3 style={{ margin: '0 0 10px 0', color: '#666', fontSize: '14px' }}>TOTAL EPISODES</h3>
              <p style={{ fontSize: '28px', fontWeight: 'bold', margin: 0, color: '#4a69dd' }}>
                {episodes.length}
              </p>
            </div>
            
            <div style={{ 
              background: 'white',
              padding: '20px',
              borderRadius: '8px',
              textAlign: 'center',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              border: '1px solid #e0e0e0'
            }}>
              <h3 style={{ margin: '0 0 10px 0', color: '#666', fontSize: '14px' }}>TOTAL LISTENS</h3>
              <p style={{ fontSize: '28px', fontWeight: 'bold', margin: 0, color: '#27ae60' }}>
                {podcast.totalListens || 0}
              </p>
            </div>
            
            <div style={{ 
              background: 'white',
              padding: '20px',
              borderRadius: '8px',
              textAlign: 'center',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              border: '1px solid #e0e0e0'
            }}>
              <h3 style={{ margin: '0 0 10px 0', color: '#666', fontSize: '14px' }}>TOTAL DURATION</h3>
              <p style={{ fontSize: '28px', fontWeight: 'bold', margin: 0, color: '#f39c12' }}>
                {formatDuration(episodes.reduce((total, ep) => total + (ep.duration || 0), 0))}
              </p>
            </div>
            
            <div style={{ 
              background: 'white',
              padding: '20px',
              borderRadius: '8px',
              textAlign: 'center',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              border: '1px solid #e0e0e0'
            }}>
              <h3 style={{ margin: '0 0 10px 0', color: '#666', fontSize: '14px' }}>EARNINGS</h3>
              <p style={{ fontSize: '28px', fontWeight: 'bold', margin: 0, color: '#8e44ad' }}>
                ${podcast.earnings || '0.00'}
              </p>
            </div>
          </div>

          {/* Recent Episodes */}
          <div>
            <h2 style={{ marginBottom: '20px' }}>Recent Episodes</h2>
            <div style={{ background: 'white', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
              {episodes.slice(0, 5).map((episode, index) => (
                <div key={episode.id} style={{ 
                  padding: '20px',
                  borderBottom: index < 4 ? '1px solid #f0f0f0' : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '15px'
                }}>
                  <div style={{ 
                    width: '60px',
                    height: '60px',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px',
                    overflow: 'hidden',
                    background: '#f0f0f0'
                  }}>
                    {podcast.imageURL ? (
                      <img 
                        src={podcast.imageURL} 
                        alt={episode.title}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      '🎙️'
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <h4 style={{ margin: '0 0 5px 0', color: '#333' }}>{episode.title}</h4>
                    <p style={{ margin: '0', color: '#666', fontSize: '14px' }}>
                      {formatDate(episode.publishedAt)} • {formatDuration(episode.duration)} • {episode.listens || 0} listens
                    </p>
                  </div>
                  <button style={{ 
                    padding: '8px 16px',
                    background: '#f5f5f5',
                    color: '#333',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}>
                    Edit
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'episodes' && (
        <div>
          <div style={{ 
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <h2 style={{ margin: 0 }}>All Episodes ({episodes.length})</h2>
            <button style={{ 
              padding: '12px 24px',
              background: '#4a69dd',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '600'
            }}>
              Add New Episode
            </button>
          </div>

          <div style={{ background: 'white', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
            {episodes.map((episode, index) => (
              <div key={episode.id} style={{ 
                padding: '25px',
                borderBottom: index < episodes.length - 1 ? '1px solid #f0f0f0' : 'none'
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px' }}>
                  <div style={{ 
                    width: '80px',
                    height: '80px',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '32px',
                    flexShrink: 0,
                    overflow: 'hidden',
                    background: '#f0f0f0'
                  }}>
                    {podcast.imageURL ? (
                      <img 
                        src={podcast.imageURL} 
                        alt={episode.title}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      '🎙️'
                    )}
                  </div>
                  
                  <div style={{ flex: 1 }}>
                    <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', color: '#333' }}>{episode.title}</h3>
                    <p style={{ 
                      margin: '0 0 12px 0', 
                      color: '#666', 
                      fontSize: '14px',
                      lineHeight: '1.4',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}>
                      {episode.description}
                    </p>
                    
                    <div style={{ 
                      display: 'flex',
                      gap: '20px',
                      fontSize: '14px',
                      color: '#888',
                      marginBottom: '15px'
                    }}>
                      <span>📅 {formatDate(episode.publishedAt)}</span>
                      <span>⏱️ {formatDuration(episode.duration)}</span>
                      <span>🎧 {episode.listens || 0} listens</span>
                      <span>👍 {episode.likes || 0} likes</span>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button style={{ 
                        padding: '8px 16px',
                        background: '#4a69dd',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}>
                        Edit Episode
                      </button>
                      <button style={{ 
                        padding: '8px 16px',
                        background: '#f5f5f5',
                        color: '#333',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}>
                        View Analytics
                      </button>
                      <a 
                        href={episode.audioURL} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ 
                          padding: '8px 16px',
                          background: '#27ae60',
                          color: 'white',
                          textDecoration: 'none',
                          borderRadius: '4px',
                          fontSize: '12px'
                        }}
                      >
                        Play Audio
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'analytics' && (
        <div>
          <h2 style={{ marginBottom: '20px' }}>Analytics</h2>
          <div style={{ 
            background: 'white',
            padding: '40px',
            borderRadius: '8px',
            textAlign: 'center',
            border: '2px dashed #ddd'
          }}>
            <div style={{ 
              width: '60px',
              height: '60px',
              background: '#f0f0f0',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px auto',
              fontSize: '24px'
            }}>
              📊
            </div>
            <h3 style={{ margin: '0 0 10px 0' }}>Advanced Analytics Coming Soon</h3>
            <p style={{ color: '#666', margin: '0' }}>
              Detailed listening analytics, engagement metrics, and performance insights will be available here.
            </p>
          </div>
        </div>
      )}

      {activeTab === 'settings' && (
        <div>
          <h2 style={{ marginBottom: '20px' }}>Podcast Settings</h2>
          <div style={{ 
            background: 'white',
            padding: '30px',
            borderRadius: '8px',
            border: '1px solid #e0e0e0',
            maxWidth: '600px'
          }}>
            <div style={{ marginBottom: '25px' }}>
              <label style={{ 
                display: 'block',
                fontWeight: '600',
                marginBottom: '8px'
              }}>
                Podcast Title
              </label>
              <input 
                type="text"
                defaultValue={podcast.title}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '16px'
                }}
              />
            </div>
            
            <div style={{ marginBottom: '25px' }}>
              <label style={{ 
                display: 'block',
                fontWeight: '600',
                marginBottom: '8px'
              }}>
                Description
              </label>
              <textarea 
                defaultValue={podcast.description}
                rows={4}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '16px',
                  resize: 'vertical'
                }}
              />
            </div>
            
            <div style={{ marginBottom: '25px' }}>
              <label style={{ 
                display: 'block',
                fontWeight: '600',
                marginBottom: '8px'
              }}>
                RSS Feed URL
              </label>
              <input 
                type="url"
                defaultValue={podcast.platformData?.rssUrl}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '16px'
                }}
              />
            </div>
            
            <div style={{ display: 'flex', gap: '10px' }}>
              <button style={{ 
                padding: '12px 24px',
                background: '#4a69dd',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '600'
              }}>
                Save Changes
              </button>
              <button style={{ 
                padding: '12px 24px',
                background: '#f5f5f5',
                color: '#333',
                border: '1px solid #ddd',
                borderRadius: '6px',
                cursor: 'pointer'
              }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PodcastDetailManagement;